import React, { useState } from 'react';
import { supabase } from '../services/supabaseService';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  InputAdornment,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import logoImage from '../ux/catlogo4.png';

// Kolorowe ikony social media
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    <path fill="#FFFFFF" d="M16.671 15.543l.532-3.47h-3.328v-2.25c0-.949.465-1.874 1.956-1.874h1.513V5.996s-1.374-.235-2.686-.235c-2.741 0-4.533 1.662-4.533 4.669v2.643H7.078v3.47h3.047v8.385a12.118 12.118 0 003.75 0v-8.385h2.796z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#000000" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const AuthModal = ({ open, onClose, onLoginSuccess, loginIntent }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Zaloguj się, 1 = Zarejestruj się
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [registrationSent, setRegistrationSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = formData.email?.trim();
    if (!email) {
      setLoginError('Wpisz adres e-mail, aby zresetować hasło.');
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin
      });
      if (error) throw error;
      setResetSent(true);
      setLoginError('');
    } catch (err) {
      console.error('Reset password error:', err);
      const msg = err?.message || err?.error_description || '';
      if (msg.includes('rate') || msg.includes('security') || msg.includes('after') || err.status === 429 || err.__isAuthError) {
        setLoginError('Zbyt wiele prób. Odczekaj minutę i spróbuj ponownie.');
      } else {
        setLoginError('Nie udało się wysłać linku. Spróbuj ponownie.');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFormData({ email: '', password: '', confirmPassword: '' });
    setEmailError('');
    setLoginError('');
    setResetSent(false);
    setRegistrationSent(false);
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (field === 'email') {
      setEmailError('');
    }
    if (field === 'password' || field === 'confirmPassword') {
      setPasswordError('');
    }
    if (field === 'email' || field === 'password') {
      setLoginError('');
    }
  };

  // Supabase import
  // Upewnij się, że masz poprawny import supabase:
  // import { supabase } from '../config/supabase';
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
    if (activeTab === 1) { // Rejestracja
      if (window.gtag) window.gtag('event', 'register_click', { event_category: 'auth' });
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Hasła nie są zgodne!');
        return;
      }
      if (formData.password.length < 6) {
        setPasswordError('Hasło musi mieć co najmniej 6 znaków');
        return;
      }

      // Rejestracja w Supabase
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: 'https://dobrynajemca.pl'
        }
      });

      if (error) {
        if (error.message.includes('already registered')) {
          setEmailError('Użytkownik o tym adresie już istnieje');
        } else if (error.message.includes('Password should be at least')) {
          setPasswordError('Hasło musi mieć co najmniej 6 znaków');
        } else {
          setLoginError('Błąd rejestracji: ' + error.message);
        }
        return;
      }

      // Supabase z email confirmation: session jest null dopóki user nie potwierdzi
      if (!data.session) {
        // Email wysłany — pokaż komunikat
        // Zapisz intent żeby po potwierdzeniu maila otworzyć formularz
        if (loginIntent) {
          localStorage.setItem('pendingIntent', loginIntent);
        }
        setRegistrationSent(true);
        setLoginError('');
        setEmailError('');
        setPasswordError('');
        if (window.gtag) window.gtag('event', 'register_email_sent', { event_category: 'auth' });
        return;
      }

      // Sesja istnieje (email confirmation wyłączone) — zaloguj od razu
      const userId = data.user.id;

      // Dodanie użytkownika do tabeli users (upsert - aktualizuje jeśli istnieje)
      const { error: insertError } = await supabase
        .from('users')
        .upsert([{
          id: userId,
          email: formData.email,
          created_at: new Date().toISOString(),
          lastlogintime: new Date().toISOString(),
          is_active: true,
          favorites: [],
          favorite_tenants: [],
        }], {
          onConflict: 'id'
        });
      if (insertError) {
        setLoginError('Błąd dodawania użytkownika do bazy: ' + insertError.message);
        return;
      }

      if (onLoginSuccess) {
        onLoginSuccess([], formData.email, userId);
      }
      if (window.gtag) window.gtag('event', 'register_success', { event_category: 'auth' });

      setLoginError('');
      setEmailError('');
      setPasswordError('');
      onClose();
    } else { // Logowanie
      if (window.gtag) window.gtag('event', 'login_click', { event_category: 'auth' });
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          setLoginError('Błąd logowania: niepoprawny email lub hasło');
        } else {
          setLoginError('Błąd logowania: ' + error.message);
        }
        return;
      }

      // Sprawdź czy wiersz użytkownika istnieje w tabeli users
      const { data: existingUserData, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('id', data.user.id)
        .single();

      if (checkError || !existingUserData) {
        // Wiersz nie istnieje — utwórz go
        const { error: insertErr } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            email: formData.email,
            lastlogintime: new Date().toISOString(),
            is_active: true,
            favorites: [],
            favorite_tenants: []
          });
        if (insertErr) {
        } else {
        }
      } else {
        // Wiersz istnieje — tylko aktualizuj lastlogintime
        await supabase
          .from('users')
          .update({ lastlogintime: new Date().toISOString() })
          .eq('id', data.user.id);
      }

      // Pobierz dane użytkownika i sprawdź czy konto jest aktywne
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('favorites, is_active, deleted_at')
        .eq('id', data.user.id)
        .single();

      if (userError) {
        // Jeśli nadal nie ma wiersza, zaloguj mimo to z pustymi ulubionymi
        if (onLoginSuccess) {
          onLoginSuccess([], formData.email, data.user.id);
        }
        onClose();
        return;
      }

      // Sprawdź czy konto jest aktywne
      if (userData.is_active === false) {
        await supabase.auth.signOut();
        setLoginError('To konto zostało dezaktywowane. Skontaktuj się z obsługą, aby je reaktywować.');
        return;
      }

      // Przekaż favorites i email do stanu globalnego (np. przez onLoginSuccess)
      if (onLoginSuccess) {
        const userId = data.user.id || data.user.uuid || null;
        onLoginSuccess((userData && userData.favorites) ? userData.favorites : [], formData.email, userId);
      }
      if (window.gtag) window.gtag('event', 'login_success', { event_category: 'auth' });
      onClose();
    }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    if (provider.toLowerCase() === 'google') {
      // Google Identity Services — popup bez supabase.co
      const GOOGLE_CLIENT_ID = '330932684578-tr12hmuinei46hhbf0298l6fhf98ducs.apps.googleusercontent.com';
      
      if (!window.google?.accounts?.id) {
        setLoginError('Google SDK nie załadowane. Odśwież stronę i spróbuj ponownie.');
        return;
      }

      try {
        // Generuj nonce dla bezpieczeństwa
        const randomBytes = new Uint8Array(32);
        crypto.getRandomValues(randomBytes);
        const nonce = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
        // Hash nonce (SHA-256) — GIS otrzyma hash, Supabase dostaje surowy nonce
        const encoder = new TextEncoder();
        const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(nonce));
        const hashedNonce = Array.from(new Uint8Array(hashBuffer), b => b.toString(16).padStart(2, '0')).join('');

        // Inicjalizuj GIS z hashed nonce
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          nonce: hashedNonce,
          callback: async (response) => {
            if (response.credential) {
              try {
                const { data, error } = await supabase.auth.signInWithIdToken({
                  provider: 'google',
                  token: response.credential,
                  nonce: nonce,
                });
                if (error) {
                  setLoginError('Błąd logowania Google: ' + error.message);
                  return;
                }
                if (data?.user) {
                  // Pobierz dane użytkownika z tabeli users
                  const { data: userData } = await supabase
                    .from('users')
                    .select('favorites')
                    .eq('id', data.user.id)
                    .single();
                  if (onLoginSuccess) {
                    onLoginSuccess(
                      userData?.favorites || [],
                      data.user.email,
                      data.user.id
                    );
                  }
                  if (window.gtag) window.gtag('event', 'login_success', { event_category: 'auth', method: 'google' });
                  onClose();
                }
              } catch (err) {
                setLoginError('Błąd logowania: ' + err.message);
              }
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Pokaż popup wyboru konta
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback: OAuth popup przez Supabase (incognito, brak cookies Google)
            supabase.auth.signInWithOAuth({
              provider: 'google',
              options: { 
                redirectTo: window.location.origin,
                queryParams: { prompt: 'select_account' }
              },
            });
          }
        });
      } catch (err) {
        setLoginError('Błąd Google: ' + err.message);
      }
    } else {
      // Facebook i inni — standardowy OAuth redirect
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider.toLowerCase(),
        options: { redirectTo: window.location.origin },
      });
      if (error) {
        setLoginError('Błąd logowania: ' + error.message);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh',
          m: isMobile ? 0 : 2
        }
      }}
    >
  <DialogContent sx={{ p: 0, overflowY: 'auto', maxHeight: '80vh', position: 'relative' }}>
        {/* Close Button w prawym górnym rogu */}
        <IconButton 
          onClick={onClose} 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#6B7280',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              color: '#374151'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Logo na górze wyśrodkowane */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          py: 3,
          borderBottom: '1px solid #E5E7EB'
        }}>
          <img 
            src={logoImage} 
            alt="Logo" 
            style={{ 
              height: '56px', 
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Header */}
        <Box sx={{ 
          py: 3,
          px: 2,
          borderBottom: '1px solid #E5E7EB',
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold', 
            color: '#1F2937',
            mb: 1,
            lineHeight: 1.3
          }}>
            {loginIntent === 'phone'
              ? 'Zaloguj się lub zarejestruj, aby zobaczyć numer telefonu'
              : loginIntent === 'message'
              ? 'Zaloguj się lub zarejestruj, aby wysyłać wiadomości'
              : loginIntent === 'favorites'
              ? 'Zaloguj się lub zarejestruj, aby dodawać ulubione'
              : loginIntent === 'profile'
              ? 'Zaloguj się na swoje konto lub zarejestruj'
              : loginIntent === 'addProfile'
              ? 'Dodaj swój profil najemcy'
              : 'Dodaj swoje ogłoszenie bez opłat'}
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {loginIntent === 'phone'
              ? 'Skontaktuj się bezpośrednio z ogłoszeniodawcą'
              : loginIntent === 'message'
              ? 'Napisz do ogłoszeniodawcy i umów się na spotkanie'
              : loginIntent === 'favorites'
              ? 'Zapisuj ogłoszenia i wracaj do nich w dowolnej chwili'
              : loginIntent === 'profile'
              ? ''
              : loginIntent === 'addProfile'
              ? 'Zajmie Ci to tylko 2 min i jest całkowicie za darmo'
              : 'Nie przepłacaj u konkurencji'}
          </Typography>
        </Box>

        <Box sx={{ 
          p: { xs: 3, sm: 4 }, 
          maxWidth: { xs: '500px', md: '650px' }, 
          margin: '0 auto' 
        }}>
          {/* Subtitle */}
          <Typography variant="body2" sx={{ 
            color: '#6B7280', 
            mb: 3,
            textAlign: 'center'
          }}>
            Monitoruj ceny, zapisuj ogłoszenia i wyszukiwania, jako pierwszy dowiaduj się o aktualizacjach
          </Typography>

          {/* Social Login Buttons */}
          <Box sx={{ mb: 3 }}>
            {/* Google */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={() => handleSocialLogin('Google')}
              sx={{
                mb: 2,
                py: 1.5,
                borderColor: '#D1D5DB',
                color: '#374151',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '500',
                '&:hover': {
                  borderColor: '#D2691E',
                  backgroundColor: 'rgba(210, 105, 30, 0.05)'
                }
              }}
            >
              Kontynuuj z Google
            </Button>

            {/* Facebook — ukryte do czasu weryfikacji Meta Business */}
            {/* <Button
              fullWidth
              variant="outlined"
              startIcon={<FacebookIcon />}
              onClick={() => handleSocialLogin('Facebook')}
              sx={{
                mb: 2,
                py: 1.5,
                borderColor: '#D1D5DB',
                color: '#374151',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '500',
                '&:hover': {
                  borderColor: '#1877F2',
                  backgroundColor: 'rgba(24, 119, 242, 0.05)'
                }
              }}
            >
              Kontynuuj z Facebookiem
            </Button> */}

            {/* Apple — ukryte do czasu konfiguracji Apple Developer */}
          </Box>

          {/* Divider z "LUB" */}
          <Box sx={{ position: 'relative', my: 3 }}>
            <Divider sx={{ borderColor: '#E5E7EB' }} />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              px: 2
            }}>
              <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: '500' }}>
                LUB
              </Typography>
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '600',
                color: '#6B7280'
              },
              '& .Mui-selected': {
                color: '#1F2937 !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#D2691E'
              }
            }}
          >
            <Tab label="Zaloguj się" />
            <Tab label="Zarejestruj się" />
          </Tabs>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Email Field */}
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              onBlur={() => setEmailTouched(true)}
              required
              error={!!emailError || (emailTouched && formData.email && (!formData.email.includes('@') || formData.email.indexOf('@') < 2 || formData.email.length - formData.email.indexOf('@') - 1 < 5))}
              helperText={emailError || (emailTouched && formData.email && (() => {
                const atIdx = formData.email.indexOf('@');
                if (!formData.email.includes('@')) return 'Adres email musi zawierać znak @';
                if (atIdx < 2) return 'Część przed @ jest za krótka (min. 2 znaki)';
                if (formData.email.length - atIdx - 1 < 5) return 'Część po @ jest za krótka (np. wp.pl)';
                return '';
              })())}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#F9FAFB'
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D2691E'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D2691E'
                },
                '& .MuiFormHelperText-root': {
                  color: '#d32f2f',
                  fontWeight: 500
                }
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              label="Hasło"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={!!passwordError}
              helperText={passwordError}
              sx={{
                mb: activeTab === 1 ? 2 : 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#F9FAFB'
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D2691E'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D2691E'
                },
                '& .MuiFormHelperText-root': {
                  color: '#d32f2f',
                  fontWeight: 500
                }
              }}
            />

            {/* Confirm Password Field - tylko dla rejestracji */}
            {activeTab === 1 && (
              <TextField
                fullWidth
                label="Potwierdź hasło"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                required
                error={!!passwordError}
                helperText={passwordError && formData.confirmPassword ? passwordError : ''}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB'
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D2691E'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D2691E'
                  }
                }}
              />
            )}

            {/* Registration confirmation */}
            {activeTab === 1 && registrationSent && (
              <Box sx={{ mb: 3, p: 2.5, backgroundColor: '#F0FDF4', borderRadius: 2, border: '1px solid #86EFAC', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#166534', fontSize: '15px', fontWeight: 600, mb: 1 }}>
                  Sprawdź swoją skrzynkę e-mail
                </Typography>
                <Typography variant="body2" sx={{ color: '#166534', fontSize: '13px', lineHeight: 1.6 }}>
                  Wysłaliśmy link potwierdzający na <strong>{formData.email}</strong>.<br />
                  Kliknij go, aby aktywować konto i zalogować się.
                </Typography>
                {loginIntent === 'addProfile' && (
                  <Typography variant="body2" sx={{ color: '#D2691E', fontSize: '13px', fontWeight: 600, mt: 1.5, lineHeight: 1.5 }}>
                    Po kliknięciu linku formularz dodawania profilu najemcy otworzy się automatycznie.
                  </Typography>
                )}
                {loginIntent === 'addListing' && (
                  <Typography variant="body2" sx={{ color: '#D2691E', fontSize: '13px', fontWeight: 600, mt: 1.5, lineHeight: 1.5 }}>
                    Po kliknięciu linku formularz dodawania ogłoszenia otworzy się automatycznie.
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '12px', mt: 1.5 }}>
                  Nie widzisz wiadomości? Sprawdź folder spam.
                </Typography>
              </Box>
            )}

            {/* Login/Register Error Message */}
            {loginError && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'error.main',
                  mb: 2,
                  textAlign: 'center',
                  fontWeight: 500
                }}
              >
                {loginError}
              </Typography>
            )}

            {/* Forgot Password Link - tylko dla logowania */}
            {activeTab === 0 && !resetSent && (
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography 
                  variant="body2" 
                  component="a" 
                  href="#"
                  onClick={handleForgotPassword}
                  sx={{ 
                    color: '#D2691E',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Nie pamiętasz hasła?
                </Typography>
              </Box>
            )}

            {/* Reset password confirmation */}
            {activeTab === 0 && resetSent && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#EFF6FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                <Typography variant="body2" sx={{ color: '#1E40AF', fontSize: '13px' }}>
                  Link do resetu hasła został wysłany na <strong>{formData.email}</strong>.<br />
                  Sprawdź skrzynkę (i folder spam).
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={resent ? undefined : async (e) => { await handleForgotPassword(e); setResent(true); setTimeout(() => setResent(false), 3000); }}
                  sx={{ 
                    color: resent ? '#16a34a' : '#1E40AF', fontSize: '12px', mt: 1.5, 
                    cursor: resent ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '4px',
                    '&:hover': resent ? {} : { textDecoration: 'underline' }
                  }}
                >
                  {resent 
                    ? <><span style={{ fontSize: '14px' }}>✓</span> Wysłano!</>
                    : <><ReplayIcon sx={{ fontSize: 15 }} /> Link nie dotarł? Wyślij ponownie</>
                  }
                </Typography>
              </Box>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || (activeTab === 1 && registrationSent)}
              sx={{
                py: 1.5,
                fontSize: '16px',
                fontWeight: '600',
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#374151',
                '&:hover': {
                  backgroundColor: '#1F2937'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#9CA3AF',
                  color: '#ffffff'
                },
                mb: 2
              }}
            >
              {loading ? 'Proszę czekać...' : (activeTab === 1 && registrationSent) ? 'E-mail wysłany ✓' : activeTab === 0 ? 'Zaloguj się' : 'Zarejestruj się'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;